import { useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Send, Loader2 } from "lucide-react";


export default function InquiryForm() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: "",
    message: "",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      // Submit to Formspree
      await axios.post("https://formspree.io/f/xeerkqwb", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        service_type: form.service_type,
        message: form.message,
        _subject: "New Quote Request - Raihan International",
        _replyto: form.email,
      }, {
        headers: { "Accept": "application/json" },
      });

   // Send to Google Sheets
      const params = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        service_type: form.service_type,
        message: form.message,
      });
      fetch(
        "https://script.google.com/macros/s/AKfycbzxPcBna5U0e5q6zqqHxUEBteDYZ5fTDdN72oBLwvU1yvfbzTugjemqiGc2cSuaMurn5w/exec?" + params.toString(),
        { method: "GET", mode: "no-cors" }
      );



      toast.success("Thank you — we'll get back to you within 24 hours");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service_type: "",
        message: "",
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="inquiry"
      data-testid="inquiry-section"
      className="py-24 md:py-32 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="space-y-6"
          >
            <p className="font-accent text-[#8D5A34] text-lg" data-testid="inquiry-accent">
              Let's work together
            </p>
            <h2
              data-testid="inquiry-heading"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1614] tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Request a Quote
            </h2>
            <Separator className="w-16 h-0.5 bg-[#D4AF37]" />
            <p
              className="text-[#1A1614]/70 text-base leading-relaxed max-w-md"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              You've spent enough time chasing suppliers who don't deliver on their word.
              Share your requirements — leather or agro — and receive a detailed, honest
              quote within 24 hours. Straightforward from the first conversation to the
              final shipment.
            </p>

            {/* Side image */}
            <div className="img-zoom rounded-2xl overflow-hidden shadow-lg mt-8 hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1759272548470-d0686d071036?auto=format&fit=crop&w=600&q=80"
                alt="Global shipping"
                className="w-full h-[280px] object-cover"
                data-testid="inquiry-image"
              />
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="inquiry-form"
              className="glass-card rounded-2xl p-8 md:p-10 space-y-6 shadow-lg"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-[#1A1614]/80 text-sm font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="inquiry-input-name"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Your full name"
                    className="bg-transparent border-b-2 border-stone-300 focus:border-[#8D5A34] px-0 py-2 rounded-none focus-visible:ring-0 placeholder:text-stone-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-[#1A1614]/80 text-sm font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="inquiry-input-email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="you@company.com"
                    className="bg-transparent border-b-2 border-stone-300 focus:border-[#8D5A34] px-0 py-2 rounded-none focus-visible:ring-0 placeholder:text-stone-400"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="phone"
                    className="text-[#1A1614]/80 text-sm font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    data-testid="inquiry-input-phone"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="+91 XXXXX XXXXX"
                    className="bg-transparent border-b-2 border-stone-300 focus:border-[#8D5A34] px-0 py-2 rounded-none focus-visible:ring-0 placeholder:text-stone-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="company"
                    className="text-[#1A1614]/80 text-sm font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    data-testid="inquiry-input-company"
                    value={form.company}
                    onChange={(e) => handleChange("company", e.target.value)}
                    placeholder="Your company"
                    className="bg-transparent border-b-2 border-stone-300 focus:border-[#8D5A34] px-0 py-2 rounded-none focus-visible:ring-0 placeholder:text-stone-400"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  className="text-[#1A1614]/80 text-sm font-medium"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Service Category *
                </Label>
                <Select
                  value={form.service_type}
                  onValueChange={(v) => handleChange("service_type", v)}
                  data-testid="inquiry-select-service"
                >
                  <SelectTrigger
                    data-testid="inquiry-select-trigger"
                    className="bg-transparent border-b-2 border-stone-300 focus:border-[#8D5A34] px-0 py-2 rounded-none focus:ring-0"
                  >
                    <SelectValue placeholder="Select a service category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="leather" data-testid="inquiry-option-leather">
                      Leather Products
                    </SelectItem>
                    <SelectItem value="agro" data-testid="inquiry-option-agro">
                      Agro Products
                    </SelectItem>
                    <SelectItem value="both" data-testid="inquiry-option-both">
                      Both - Leather & Agro
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="text-[#1A1614]/80 text-sm font-medium"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Your Requirements *
                </Label>
                <Textarea
                  id="message"
                  data-testid="inquiry-input-message"
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder="Describe the products you need, quantities, and any specifications..."
                  rows={5}
                  className="bg-transparent border-b-2 border-stone-300 focus:border-[#8D5A34] px-0 py-2 rounded-none focus-visible:ring-0 placeholder:text-stone-400 resize-none"
                />
              </div>

              <Button
                type="submit"
                data-testid="inquiry-submit-btn"
                disabled={loading}
                className="rounded-full w-full px-8 py-4 h-auto bg-[#8D5A34] text-white hover:bg-[#5D3A20] transition-all hover:scale-[1.02] shadow-lg text-base font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Submit Inquiry
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
